import React, { createProvider } from 'reactn';
import MenuButton from '../../components/MenuButton';
import PrivateBlock from '../../components/PrivateBlock';
import { withStyles } from '@material-ui/core/styles';
import GridList from '@material-ui/core/GridList';
import GridListTile from '@material-ui/core/GridListTile';
import DashboardContext from '../../contexts/DashboardContext';
import UserContext from '../../contexts/UserContext';
import { Match } from '@reach/router';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import gql from 'graphql-tag';
import { CHECKLIST } from '../../common/constants';
import { UPDATE_PERIOD_PLAN_RESULT } from '../../common/graphql/UpdatePeriodPlanResult';
import { useMutation, useQuery } from 'react-apollo-hooks';
import getMemberPpr from '../../common/graphql/GetMemberPeriodPlanResult.js';
import {
  onUpdatePeriodPlanResult,
  onCreatePeriodPlanResult
} from '../../graphql/subscriptions';
import { API, graphqlOperation } from 'aws-amplify';
import { PAGE_KEYS } from '../../contexts/DashboardContext/pages';

const MenuProvider = createProvider({
  tickerFn: null
});

export function useMenuTicker() {
  const [tick, setTickerFn] = MenuProvider.useGlobal('tickerFn');
  return { tick, setTickerFn };
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper
  },
  gridList: {},
  icon: {
    color: 'rgba(255, 255, 255, 0.54)'
  }
});

const MenuWrapper = ({ children, allowedGroups, onIsAllowed }) => {
  if (allowedGroups)
    return (
      <PrivateBlock allowedGroups={allowedGroups} onIsAllowed={onIsAllowed}>
        {children}
      </PrivateBlock>
    );
  else return children;
};

const myPprAlertsQuery = gql`
  query($memberId: ID!, $periodId: ID!) {
    searchPeriodPlanResults(
      nextToken: 0
      sort: { field: createdAt, direction: desc }
      filter: {
        periodPlanResultMemberId: { eq: $memberId }
        periodPlanResultPeriodId: { eq: $periodId }
        or: [{ checklist: { exists: true } }, { alerts: { exists: true } }]
      }
    ) {
      nextToken
      items {
        id
        checklist
        alerts
      }
    }
  }
`;

const MenuList = props => {
  const [checked, setChecked] = React.useState([]);
  const [alerts, setAlerts] = React.useState([]);
  const [lastAlerts, setLastAlerts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [enabledMenus, setEnabledMenus] = React.useState({});
  const [triggerAnimation, setTriggerAnimation] = React.useState(false);
  const { classes } = props;
  const { service, state, previousPeriod: lastWeekPeriod, ...rest } = React.useContext(
    DashboardContext
  );
  const { currentUser } = React.useContext(UserContext);
  console.log('CURRENT USER: ', currentUser);
  console.log('CURRENT USER PPR: ', rest.currentUserPpr);
  const { data: lastPprData, loading: lastLoading } = useQuery(getMemberPpr().query, {
    fetchPolicy: 'network-only',
    skip: !lastWeekPeriod,
    variables: {
      memberId: currentUser.username,
      periodId: get(lastWeekPeriod, 'id')
    }
  });
  // useMyPpr({ memberId: currentUser.username, periodId: get(lastWeekPeriod, 'id') });

  React.useEffect(() => {
    const handleSub = value => {
      const lastPprId = get(lastPprData, 'getMember.planResults.items.0.id');
      if (lastPprData && get(value, 'data.onUpdatePeriodPlanResult.id') === lastPprId)
        setLastAlerts(get(value, 'data.onUpdatePeriodPlanResult.alerts') || []);
    };
    const updatePprSub = API.graphql(
      graphqlOperation(onUpdatePeriodPlanResult)
    ).subscribe({
      next: ({ value }) => handleSub(value)
    });
    const createPprSub = API.graphql(
      graphqlOperation(onCreatePeriodPlanResult)
    ).subscribe({
      next: ({ value }) => handleSub(value)
    });
    return () => {
      updatePprSub.unsubscribe();
      createPprSub.unsubscribe();
    };
  }, [lastPprData]);

  const menus = get(state, 'context.pages', []);
  const updateChecklist = useMutation(UPDATE_PERIOD_PLAN_RESULT.mutation);
  const { data: pprAlertHistory } = useQuery(myPprAlertsQuery, {
    variables: {
      memberId: currentUser.username,
      periodId: get(rest, 'currentUserPpr.periodPlanResultPeriodId')
    },
    fetchPolicy: 'network-only'
  });

  const { tick, setTickerFn } = useMenuTicker();

  React.useEffect(() => {
    if (!rest.currentUserPpr.loading) {
      const cList = get(rest, 'currentUserPpr.checklist') || [],
        mAlerts = get(rest, 'currentUserPpr.alerts') || [];

      get(pprAlertHistory, 'searchPeriodPlanResults.items', []).forEach(pprItem => {
        cList.push(...(pprItem.checklist || []));
        mAlerts.push(...(pprItem.alerts || []));
      });
      setChecked(uniq(cList));
      setAlerts(uniq(mAlerts));
      setLoading(false);
    }
  }, [rest.currentUserPpr, pprAlertHistory, rest]);

  React.useEffect(() => {
    if (!lastLoading && lastPprData) {
      setLastAlerts(get(lastPprData, 'getMember.planResults.items.0.alerts') || []);
    }
  }, [lastLoading, lastPprData]);

  React.useEffect(() => {
    setTickerFn(async key => {
      const { ...ppr } = rest.currentUserPpr;
      const hasPpr = Object.keys(ppr).length !== 0;
      if (hasPpr && CHECKLIST.includes(`/${key}`) && !checked.includes(`/${key}`)) {
        setTriggerAnimation(key);
        try {
          await updateChecklist({
            variables: {
              input: {
                id: ppr.id,
                checklist: [...checked, `/${key}`],
                alerts: alerts.filter(alert => alert !== `/${key}`)
              }
            }
          });
        } catch (e) {
          console.log('ERROR UPDATING PPR CHECKLIST', e);
        }
      }
    });
  }, [rest.currentUserPpr, checked, alerts, setTickerFn, updateChecklist]);

  async function handleMenuChange(key) {
    service.send({ type: 'CHANGE_PAGE', value: key });
    const {
      // loading,
      ...ppr
    } = rest.currentUserPpr;

    const hasPpr = Object.keys(ppr).length !== 0;

    if (hasPpr && (lastAlerts.includes(`/${key}`) || alerts.includes(`/${key}`))) {
      try {
        await updateChecklist({
          variables: {
            input: {
              id:
                key === PAGE_KEYS.SHOP_TALK
                  ? get(lastPprData, 'getMember.planResults.items.0.id')
                  : ppr.id,
              alerts: alerts.filter(alert => alert !== `/${key}`)
            }
          }
        });
      } catch (e) {
        console.log('ERROR UPDATING PPR CHECKLIST', e);
      }
    }

    //wab should be manually ticked
    if (key !== PAGE_KEYS.WAB) {
      tick(key);
    }
  }
  return (
    <GridList cellHeight={115} spacing={0} className={classes.gridList}>
      {menus.map(menu => {
        const matcher = menu.path + (menu.path === '/' ? '' : '/*');
        if (enabledMenus[menu.key] === false) return null;
        return (
          <GridListTile key={menu.key}>
            <Match path={matcher}>
              {({ match }) => (
                <MenuWrapper
                  allowedGroups={menu.allowedGroups}
                  onIsAllowed={isAllowed => {
                    setEnabledMenus(oldState => ({ ...oldState, [menu.key]: isAllowed }));
                  }}>
                  <MenuButton
                    label={menu.label}
                    id={menu.key}
                    active={match}
                    onClick={handleMenuChange}
                    triggerAnimation={triggerAnimation}
                    hasAlert={(() => {
                      if (menu.key === 'coach-corner')
                        return lastAlerts.includes(menu.path);
                      return alerts.includes(menu.path);
                    })()}
                    hasUnread={(() => {
                      if (
                        currentUser.memberInfo.role.group !== 'Admins' &&
                        CHECKLIST.includes(menu.path) &&
                        !loading
                      ) {
                        return checked.includes(menu.path);
                      }
                      return null;
                    })()}>
                    {menu.icon}
                  </MenuButton>
                </MenuWrapper>
              )}
            </Match>
          </GridListTile>
        );
      })}
    </GridList>
  );
};

export default withStyles(styles)(MenuList);
